import React, { useState, useEffect } from 'react';
import { RefreshCw, Download, X } from 'lucide-react';
import { skipWaiting } from '../serviceWorkerRegistration';

const PWAUpdatePrompt: React.FC = () => {
  const [showUpdatePrompt, setShowUpdatePrompt] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);

  useEffect(() => {
    const handleServiceWorkerUpdate = () => {
      setShowUpdatePrompt(true);
    };

    // Listen for service worker updates
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.addEventListener('message', (event) => {
        if (event.data && event.data.type === 'SW_UPDATE_AVAILABLE') {
          handleServiceWorkerUpdate();
        }
      });

      // Check for updates every 30 minutes when app is active
      const updateInterval = setInterval(() => {
        navigator.serviceWorker.ready.then((registration) => {
          registration.update();
        });
      }, 30 * 60 * 1000); // 30 minutes

      return () => {
        clearInterval(updateInterval);
      };
    }
  }, []);

  const handleUpdate = async () => {
    setIsUpdating(true);
    
    try {
      // Skip waiting and activate new service worker
      skipWaiting();
      
      // Reload the page to get the new version
      setTimeout(() => {
        window.location.reload();
      }, 1000);
    } catch (error) {
      // console.error('Error updating app:', error);
      setIsUpdating(false);
    }
  };

  const handleDismiss = () => {
    setShowUpdatePrompt(false);
  };

  if (!showUpdatePrompt) {
    return null;
  }

  return (
    <div className="fixed top-4 left-4 right-4 md:left-auto md:right-4 md:w-80 z-50">
      <div className="bg-white border border-primary-200 rounded-lg shadow-lg p-4">
        <div className="flex items-start space-x-3">
          <div className="flex-shrink-0">
            <div className="w-8 h-8 bg-red-500 rounded-full flex items-center justify-center">
              <RefreshCw className="w-4 h-4 text-white" />
            </div>
          </div>
          
          <div className="flex-1 min-w-0">
            <h3 className="text-sm font-medium text-black">
              App Update Available
            </h3>
            <p className="text-sm text-primary-600 mt-1">
              A new version is ready with bug fixes and improvements.
            </p>
            
            <div className="flex space-x-2 mt-3">
              <button
                onClick={handleUpdate}
                disabled={isUpdating}
                className="inline-flex items-center space-x-1 px-3 py-1.5 bg-black hover:bg-gray-800 disabled:bg-gray-400 text-white text-xs font-medium rounded transition-colors"
              >
                {isUpdating ? (
                  <>
                    <RefreshCw className="w-3 h-3 animate-spin" />
                    <span>Updating...</span>
                  </>
                ) : (
                  <>
                    <Download className="w-3 h-3" />
                    <span>Update Now</span>
                  </>
                )}
              </button>
              
              <button
                onClick={handleDismiss}
                className="inline-flex items-center space-x-1 px-3 py-1.5 bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 text-xs font-medium rounded transition-colors"
              >
                <X className="w-3 h-3" />
                <span>Later</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PWAUpdatePrompt;
